import React, { useCallback } from 'react';
import StyledReactSelect from '../../../components/inputs/styled-react-select';
import { stringifyStatus } from '../../../helpers/type-formatters';
import { OrderingDirection, OrderingType, Status } from '../../../logic/graphql/types/types.d';
import { useIntl } from 'react-intl';

type ListControlsProps = {
  resetList: () => void;
  statusOptions: {
    value: string;
    label: string;
  }[];
  selectedStatus: Status | 'all';
  setSelectedStatus: (status: Status | 'all') => void;
  orderingOptions: {
    value: { direction: OrderingDirection; type: OrderingType };
    label: string;
  }[];
  selectedOrderingType: OrderingType;
  selectedOrderingDirection: OrderingDirection;
  istrumentPlaceholder: string;
  instrumentOptions: {
    value: string;
    label: string;
  }[];
  selectedInstruments?: string[];
  setSelectedInstruments: (instuments: string[]) => void;
  setSelectedOrderingType: (type: OrderingType) => void;
  setSelectedOrderingDirection: (direction: OrderingDirection) => void;
};

const ListControls = (props: ListControlsProps): JSX.Element => {
  const intl = useIntl();
  const {
    selectedStatus,
    setSelectedStatus,
    selectedOrderingType,
    selectedOrderingDirection,
    setSelectedOrderingDirection,
    setSelectedOrderingType,
    selectedInstruments,
    setSelectedInstruments,
    resetList,
  } = props;

  const handleStatusChange = useCallback(
    (value) => {
      if ((value as Status | 'all') !== selectedStatus) {
        resetList();
        setSelectedStatus(value as Status | 'all');
      }
    },
    [selectedStatus, setSelectedStatus, resetList],
  );

  const handleOrderingChange = useCallback(
    (value) => {
      const selected = value as { direction: OrderingDirection; type: OrderingType };
      if (
        selected.type !== selectedOrderingType ||
        selected.direction !== selectedOrderingDirection
      ) {
        resetList();
        setSelectedOrderingType(selected.type);
        setSelectedOrderingDirection(selected.direction);
      }
    },
    [
      selectedOrderingType,
      selectedOrderingDirection,
      setSelectedOrderingType,
      setSelectedOrderingDirection,
      resetList,
    ],
  );

  const handleInstrumentChange = useCallback(
    (instruments) => {
      resetList();
      setSelectedInstruments(instruments as string[]);
    },
    [setSelectedInstruments, resetList],
  );

  return (
    <>
      <StyledReactSelect
        type="thin-ice"
        options={props.statusOptions}
        onChange={handleStatusChange}
        defaultValue={{ value: Status.Active, label: stringifyStatus(Status.Active, intl) }}
        className="full-width-mobile mb-1 sm:mb-0 sm:mr-3"
      />
      <StyledReactSelect
        type="thin-ice"
        options={props.orderingOptions}
        onChange={handleOrderingChange}
        defaultValue={props.orderingOptions[0]}
        className="full-width-mobile mb-1 sm:mb-0 sm:mr-3"
      />
      <StyledReactSelect
        placeholder={props.istrumentPlaceholder}
        type="thin-ice"
        searchable
        isMulti
        value={selectedInstruments && selectedInstruments.length === 0 ? null : undefined}
        options={props.instrumentOptions}
        onChange={handleInstrumentChange}
        className="full-width-mobile mb-3 sm:mb-0 sm:mr-3"
      />
    </>
  );
};

export default ListControls;
